import React, { useState, useEffect, useCallback } from 'react';
import DataTable from 'datatables.net-react';
import DT from 'datatables.net-bs5';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'datatables.net-bs5/css/dataTables.bootstrap5.min.css';
import 'datatables.net-responsive-bs5/css/responsive.bootstrap5.min.css';
import 'flag-icons/css/flag-icons.min.css';
import ModalAnnonce from './ModalAnnonce';
import '../../../styles/Datatable.scss';
import { API_URL } from '../../../config';
import { getCountryCode } from '../../../utils/countryFlags';

DataTable.use(DT);

const DatatableAnnonce = ({ announcements, users }) => {
  const [tableData, setTableData] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [cityCountryMap, setCityCountryMap] = useState({});

  // Récupérer les villes pour construire le mapping ville → pays
  useEffect(() => {
    fetch(`${API_URL}/api/city/getcities`)
      .then(res => res.json())
      .then((result) => {
        if (result.status === 0 && result.cities) {
          const map = {};
          result.cities.forEach(city => {
            if (city.name && city.country) {
              map[city.name] = city.country;
            }
          });
          setCityCountryMap(map);
        }
      })
      .catch(() => {});
  }, []);

  const transformData = useCallback((data, usersData, cityMap) => {
    if (!data) return [];

    const currentDate = new Date();

    const findObjectById = (array, id) => {
      return array.find(item => item._id === id) || null;
    };

    return Object.keys(data)
      .flatMap((key) =>
        data[key].map((item) => {
          const formatDate = (dateString) => {
            const date = new Date(dateString);
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${day}/${month}/${year}`;
          };
          const formatDateTime = (dateString) => {
            const date = new Date(dateString);
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            return `${day}/${month}/${year} ${hours}:${minutes}`;
          };

          const status =
            new Date(item.dateOfDeparture) < currentDate
              ? 'Expiré'
              : item.active && !item.locked
              ? 'Activé'
              : item.locked
              ? 'Bloqué'
              : 'En attente';

          const currentUser = findObjectById(usersData, item.userId);

          // Chercher le pays via startCity2 ou via le mapping ville → pays
          const startCountry = item.startCity2?.country || cityMap[item.startCity] || '';
          const endCountry = item.endCity2?.country || cityMap[item.endCity] || '';
          const startIso = getCountryCode(startCountry);
          const endIso = getCountryCode(endCountry);

          return [
            item.status === 'container' ? 'Conteneur' : 'Kilo',
            item.startCity,
            item.endCity,
            formatDate(item.dateOfDeparture),
            formatDateTime(item.date),
            status,
            '<button class="btn btn-info btn-sm voir-plus" type="button" data-bs-toggle="modal" data-bs-target="#detailModal">Voir</button>',
            item.draft ? item.draft[0] : null,
            item._id,
            currentUser?.name,
            currentUser?.email,
            new Date(item.date).getTime(),
            item.transitaire || null,
            startIso || '',
            endIso || '',
          ];
        })
      )
      .sort((a, b) => b[11] - a[11]);
  }, []);


  useEffect(() => {
    if (announcements && users && Object.keys(cityCountryMap).length > 0) {
      setTableData(transformData(announcements, users, cityCountryMap));
    }
  }, [announcements, users, cityCountryMap, transformData]);

  const renderCityWithFlag = (data, type, row, meta) => {
    if (type !== 'display') return data;
    const isoIndex = meta.col === 1 ? 13 : 14;
    const iso = row[isoIndex];
    if (iso) {
      return `<span class="fi fi-${iso}" style="margin-right:6px"></span>${data}`;
    }
    return data;
  };

  const columns = [
    { title: 'Type', data: 0 },
    { title: 'Départ', data: 1, render: renderCityWithFlag },
    { title: 'Arrivée', data: 2, render: renderCityWithFlag },
    { title: 'Départ le', data: 3 },
    { title: 'Créé le', data: 4 },
    {
      title: 'Statut',
      data: 5,
      render: (statut) => {
        switch (statut) {
          case 'En attente':
            return '<span class="text-warning">En attente</span>';
          case 'Activé':
            return '<span class="text-success">Activé</span>';
          case 'Expiré':
            return '<span class="text-danger">Expiré</span>';
            case 'Bloqué':
              return '<span class="text-danger">Bloqué</span>';
          default:
            return statut;
        }
      },
    },
    {
      title: 'Action',
      data: 6,
      render: (data) => data,
    },
  ];

  const options = {
    order: [[11, 'desc']],
    language: {
      processing: 'Traitement en cours...',
      search: 'Rechercher&nbsp;:',
      lengthMenu: 'Afficher _MENU_ éléments',
      info: "Affichage de l'élément _START_ à _END_ sur _TOTAL_ éléments",
      infoEmpty: "Affichage de l'élément 0 à 0 sur 0 éléments",
      infoFiltered: '(filtré de _MAX_ éléments au total)',
      loadingRecords: 'Chargement en cours...',
      zeroRecords: 'Aucun élément à afficher',
      emptyTable: 'Aucune donnée disponible dans le tableau',
      paginate: {
        first: 'Premier',
        previous: 'Précédent',
        next: 'Suivant',
        last: 'Dernier',
      },
    },
    createdRow: (rowNode, rowData) => {
      const button = rowNode.querySelector('.voir-plus');
      if (button) {
        button.addEventListener('click', () => {
          setSelectedRow(rowData);
        });
      }
    },
  };

  return (
    <>
      <DataTable
        data={tableData}
        columns={columns}
        options={options}
        className="table table-striped"
        style={{ width: '100%' }}
      />
      <ModalAnnonce selectedRow={selectedRow} />
    </>
  );
};

export default DatatableAnnonce;
